import { CheckCircle2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Department } from '@/lib/departments'
import { DEPARTMENT_BY_VALUE } from '@/lib/departments'
import {
  buildRecommendedActions,
  formatBudgetRange,
  formatConfidence,
  parseClassification,
  parseRoutingDecision,
  urgencyBadgeClass,
  type ParsedClassification,
} from '@/lib/inbox-classification'

interface AttachmentMeta {
  id: string
  filename: string
  sizeBytes?: number | null
}

interface InboxSummaryCardProps {
  classification: Record<string, unknown> | null
  routingDecision?: Record<string, unknown> | null
  inquiryId?: string | null
  assignedDepartment?: Department | null
  assignedName?: string | null
  routedToName?: string | null
  attachments?: AttachmentMeta[]
  className?: string
}

function KeyValue({
  label,
  value,
}: {
  label: string
  value: string | null | undefined
}) {
  if (!value) return null
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-ovation-muted">
        {label}
      </div>
      <div className="text-sm text-ovation-ink">{value}</div>
    </div>
  )
}

function ExtractedFieldsGrid({ fields }: { fields: ParsedClassification['extractedFields'] }) {
  const budget = formatBudgetRange(fields.budgetMin, fields.budgetMax)
  const location = [fields.eventCity, fields.eventCountry].filter(Boolean).join(', ')
  const hasAny =
    fields.contactName ||
    fields.contactOrg ||
    fields.eventDate ||
    location ||
    fields.venueName ||
    fields.venueCapacity != null ||
    budget

  if (!hasAny) return null

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <KeyValue label="Contact" value={fields.contactName} />
      <KeyValue label="Organisation" value={fields.contactOrg} />
      <KeyValue label="Event date" value={fields.eventDate} />
      <KeyValue label="Location" value={location || null} />
      <KeyValue label="Venue" value={fields.venueName} />
      <KeyValue
        label="Capacity"
        value={
          fields.venueCapacity != null
            ? fields.venueCapacity.toLocaleString()
            : null
        }
      />
      <KeyValue label="Budget" value={budget} />
    </div>
  )
}

export function InboxSummaryCard({
  classification: classificationRaw,
  routingDecision,
  inquiryId,
  assignedDepartment,
  assignedName,
  routedToName,
  attachments = [],
  className,
}: InboxSummaryCardProps) {
  const classification = classificationRaw
    ? parseClassification(classificationRaw)
    : null
  const routing = routingDecision
    ? parseRoutingDecision(routingDecision, inquiryId)
    : null

  const displayAssignee =
    assignedName ?? routedToName ?? routing?.forwardedTo ?? null
  const displayDepartment =
    assignedDepartment ?? routing?.targetDepartment ?? null
  const departmentLabel = displayDepartment
    ? (DEPARTMENT_BY_VALUE[displayDepartment]?.label ?? displayDepartment)
    : routing?.departmentLabel ?? null

  const recommended = buildRecommendedActions({
    classification,
    department: displayDepartment,
    assignedName: displayAssignee,
  })

  if (!classification && !routing) {
    return (
      <div
        className={cn(
          'rounded-lg border border-dashed border-ovation-ink/15 bg-ovation-paper/30 p-4 text-sm text-ovation-muted',
          className,
        )}
      >
        No AI analysis yet. Use Reclassify to run classification.
      </div>
    )
  }

  const secondary =
    classification?.secondaryTypes.length
      ? ` → ${classification.secondaryTypes.join(', ')}`
      : ''

  return (
    <section
      className={cn(
        'overflow-hidden rounded-lg border border-ovation-ink/10 bg-gradient-to-b from-ovation-paper/50 to-white',
        className,
      )}
    >
      <div className="grid gap-4 border-b border-ovation-ink/10 p-4 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-ovation-muted">
            Category
          </div>
          <div className="mt-0.5 font-medium text-ovation-ink">
            {classification
              ? `${classification.primaryLabel}${secondary}`
              : '—'}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-ovation-muted">
            Priority
          </div>
          <div className="mt-1">
            {classification?.urgency ? (
              <span
                className={cn(
                  'inline-flex rounded px-2 py-0.5 text-xs font-medium',
                  urgencyBadgeClass(classification.urgency),
                )}
              >
                {classification.urgencyLabel}
              </span>
            ) : (
              <span className="text-sm text-ovation-muted">—</span>
            )}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-ovation-muted">
            Assigned to
          </div>
          <div className="mt-0.5 text-sm text-ovation-ink">
            {displayAssignee ?? departmentLabel ?? 'Unassigned'}
          </div>
          {displayAssignee && departmentLabel && (
            <div className="text-xs text-ovation-muted">{departmentLabel}</div>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-ovation-muted">
            AI confidence
          </div>
          <div className="mt-0.5 text-sm font-medium text-ovation-ink">
            {formatConfidence(classification?.confidence ?? null) ?? '—'}
          </div>
        </div>
      </div>

      {classification?.summary && (
        <div className="border-b border-ovation-ink/10 px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-ovation-muted">
            Executive summary
          </div>
          <p className="mt-1 text-sm leading-relaxed text-ovation-ink">
            {classification.summary}
          </p>
        </div>
      )}

      {classification && (
        <div className="border-b border-ovation-ink/10 px-4 py-3">
          <div className="mb-2 text-[10px] uppercase tracking-wider text-ovation-muted">
            Key information
          </div>
          <ExtractedFieldsGrid fields={classification.extractedFields} />
          {attachments.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-wider text-ovation-muted">
                Attachments
              </div>
              <div className="mt-1 text-sm text-ovation-ink">
                {attachments.map((a) => a.filename).join(', ')}
              </div>
            </div>
          )}
          {classification.routingGroups.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {classification.routingGroups.map((g) => (
                <span
                  key={g}
                  className="rounded bg-ovation-ink/5 px-2 py-0.5 text-[11px] text-ovation-ink/80"
                >
                  {g}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {routing && (
        <div className="border-b border-ovation-ink/10 px-4 py-3">
          <div className="text-[10px] uppercase tracking-wider text-ovation-muted">
            Routing
          </div>
          <div className="mt-1 text-sm text-ovation-ink">
            {routing.ruleName ? (
              <span className="font-medium">{routing.ruleName}</span>
            ) : null}
            {routing.ruleName ? ' · ' : ''}
            {routing.actionLabel}
            {routing.forwardedTo ? ` → ${routing.forwardedTo}` : ''}
          </div>
          {inquiryId && (
            <div className="mt-1 text-xs text-emerald-700">
              Linked inquiry created
            </div>
          )}
        </div>
      )}

      {recommended.length > 0 && (
        <div className="px-4 py-3">
          <div className="mb-2 text-[10px] uppercase tracking-wider text-ovation-muted">
            Recommended actions
          </div>
          <ul className="space-y-1.5">
            {recommended.map((action) => (
              <li
                key={action}
                className="flex items-start gap-2 text-sm text-ovation-ink"
              >
                <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />
                {action}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}
